import { useCallback, useEffect } from 'react';
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  CurrentUserDto,
  InvitedUserDto,
  InviteUserBodyDto,
  InviteUserResponseDto,
  UpdateProfilePictureDto,
  PlainOrganizationListDto,
  UpdateUserRoleDtoRoleEnum,
  OrganizationUserDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { PaginationOptionsWithSortArray } from '@sib/api/types';
import { useUserSetupStore, useAuthStore } from '@sib/auth';
import { AxiosError } from 'axios';

const useSetupUser = () => {
  const {
    setOrganizations,
    setOrganization,
    organization: { id: organizationId },
    setHasGlobalAccess,
    setGlobalActionPermissions,
    isSetupCompleted,
    setIsSetupCompleted,
  } = useUserSetupStore();

  const setupUser = useCallback(
    (data: CurrentUserDto | null | undefined) => {
      if (!data) return;

      if (data.permissions) setGlobalActionPermissions(data.permissions);

      if (isSetupCompleted) return;

      const { hasGlobalAccess } = data;

      setHasGlobalAccess(hasGlobalAccess);

      if (data?.organizations?.length && !hasGlobalAccess)
        try {
          setOrganizations(data.organizations);

          const newOrganization = data.organizations[0];

          const shouldAssignNewOrganization =
            !data.organizations.some(
              organization => organization.id === organizationId
            ) && !hasGlobalAccess;
          if (shouldAssignNewOrganization)
            setOrganization({
              name: newOrganization.name,
              id: newOrganization.id,
            });

          setIsSetupCompleted(true);
        } catch {
          return setOrganization({
            id: '',
            name: '',
          });
        }

      if (hasGlobalAccess) setIsSetupCompleted(true);
    },
    [
      organizationId,
      setOrganizations,
      setOrganization,
      setHasGlobalAccess,
      setGlobalActionPermissions,
      isSetupCompleted,
      setIsSetupCompleted,
    ]
  );

  return setupUser;
};

export const useGetUser = () => {
  const setupUser = useSetupUser();
  const { setAccount } = useAuthStore();

  const queryResult = useQuery<CurrentUserDto, AxiosError>({
    queryKey: [QueryKey.Resource.User],
    queryFn: async () => {
      const data = await api.user.usersControllerGetCurrentUser();

      setupUser(data);

      return data;
    },
  });

  useEffect(() => {
    const status = queryResult.error?.response?.status;

    if (status === 403 || status === 404) setAccount(null);
  }, [queryResult.error, setAccount]);

  return queryResult;
};

export const useGetUserOrganizations = (
  paginationOptions: PaginationOptionsWithSortArray,
  options?: UseQueryOptions<PlainOrganizationListDto>
): UseQueryResult<PlainOrganizationListDto> =>
  useQuery({
    placeholderData: keepPreviousData,
    ...options,
    queryKey: [
      QueryKey.Resource.UserOrganizations,
      paginationOptions.page,
      paginationOptions.filter,
    ],

    queryFn: () =>
      api.user.usersControllerGetUserOrganizations(paginationOptions),
  });

export const useInfiniteOrganizations = (
  { page, size, sort, filter }: PaginationOptionsWithSortArray,
  options?: Partial<
    UseInfiniteQueryOptions<
      PlainOrganizationListDto,
      Error,
      InfiniteData<PlainOrganizationListDto>
    >
  >
) => {
  const {
    hasGlobalAccess,
    organization: { id: organizationId },
    setOrganization,
  } = useUserSetupStore();

  return useInfiniteQuery({
    ...options,
    queryKey: [QueryKey.Resource.UserOrganizations, size, sort, filter],
    queryFn: async ({ pageParam }) => {
      const response = await api.user.usersControllerGetUserOrganizations({
        page: Number(pageParam),
        size,
        ...(sort && { sort }),
        ...(filter?.length && { filter }),
      });

      if (!organizationId && hasGlobalAccess && response.items.length > 0) {
        const newOrganization = response.items[0];
        setOrganization({
          id: newOrganization.id,
          name: newOrganization.name,
        });
      }

      return response;
    },
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: page || 1,
    enabled: !!hasGlobalAccess,
  });
};

export const useGetOrganizationInvitedUsers = (
  organizationId: string,
  options?: { enabled?: boolean }
) =>
  useQuery<InvitedUserDto[]>({
    queryKey: [QueryKey.Resource.InvitedUsers, organizationId],
    queryFn: async () =>
      await api.organization.accessShareControllerGetInvitedUsers(
        organizationId,
        {
          withAccessToken: true,
        }
      ),
    enabled: options?.enabled ?? !!organizationId,
  });

export const useInviteUser = (
  options?: UseMutationOptions<
    InviteUserResponseDto,
    Error,
    InviteUserBodyDto & { organizationId: string }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({ organizationId, ...data }) =>
      api.organization.accessShareControllerInviteUsers(organizationId, data, {
        withAccessToken: true,
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.InvitedUsers, variables.organizationId],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

interface DeleteInvitedUserPayload {
  userId: string;
}

export const useDeleteInvitedUser = (
  options?: UseMutationOptions<void, Error, DeleteInvitedUserPayload>
) =>
  useMutation({
    ...options,
    mutationFn: async ({ userId }) =>
      api.user.accessShareControllerDeleteInvitedUser(userId, {
        withAccessToken: true,
      }),
  });

export interface ResendInvitationPayload {
  userId: string;
  organizationId: string;
}

export const useResendInvitation = (
  options?: UseMutationOptions<void, Error, ResendInvitationPayload>
) =>
  useMutation({
    ...options,
    mutationFn: async ({ userId, organizationId }) =>
      api.organization.accessShareControllerResendInvitation(
        userId,
        organizationId,
        { withAccessToken: true }
      ),
  });

interface UpdateProfilePictureResponseDto {}

export const useUploadProfilePicture = (
  options?: UseMutationOptions<
    UpdateProfilePictureResponseDto,
    Error,
    UpdateProfilePictureDto
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.User],
      });
      options?.onSuccess?.(data, variables, context);
    },
    mutationFn: data =>
      api.profilePicture.profilePictureControllerUpdateTicket(
        data
      ) as unknown as Promise<UpdateProfilePictureResponseDto>,
  });
};

interface UseUpdateUserRolePayload {
  userId: string;
  role: UpdateUserRoleDtoRoleEnum;
}

export interface UseUpdateUserRoleOptions
  extends UseMutationOptions<
    OrganizationUserDto,
    Error,
    UseUpdateUserRolePayload
  > {
  organizationId: string;
}

export const useUpdateUserRole = ({
  organizationId,
  ...options
}: UseUpdateUserRoleOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ userId, role }: UseUpdateUserRolePayload) =>
      api.organization.userManagementControllerUpdateUserRole(
        userId,
        organizationId,
        { role }
      ),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.OrganizationUsers, organizationId],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
